import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Monitor, 
  CreditCard, 
  Settings, 
  Server, 
  Sparkles, 
  Gauge,
  Lock,
  Users,
  Eye,
  FileText,
  Smartphone,
  Laptop,
  Tablet,
  Globe,
  Zap,
  Package,
  BarChart3,
  Scan,
  Receipt,
  ArrowLeftRight,
  Database,
  Cloud,
  CheckCircle2
} from 'lucide-react';

export default function SystemSpecsPage() {
  const specifications = [
    {
      id: 1,
      title: "Security Features",
      icon: Shield,
      color: "text-red-500",
      bgColor: "bg-red-500/10",
      features: [
        {
          name: "JWT Authentication",
          description: "Industry-standard JSON Web Token authentication for secure user sessions with 24-hour token expiration",
          icon: Lock
        },
        {
          name: "Password Encryption",
          description: "All passwords are hashed using bcrypt algorithm with salt - passwords are never stored in plain text",
          icon: Shield
        },
        {
          name: "Role-Based Access Control (RBAC)",
          description: "5 distinct user roles with granular permissions: Super Admin, Org Admin, Inventory Manager, Warehouse Staff, Auditor",
          icon: Users
        },
        {
          name: "Multi-Tenant Data Isolation",
          description: "Complete data separation between organizations - each tenant's data is isolated and protected",
          icon: Database
        },
        {
          name: "Audit Logging",
          description: "Comprehensive audit trail tracking all user actions, changes, and system events with timestamps",
          icon: Eye
        },
        {
          name: "Input Validation",
          description: "All API inputs are validated using Pydantic models to prevent injection attacks and data corruption",
          icon: FileText
        }
      ]
    },
    {
      id: 2,
      title: "Customer Interface",
      icon: Monitor,
      color: "text-blue-500",
      bgColor: "bg-blue-500/10",
      features: [
        {
          name: "Modern React Dashboard",
          description: "Clean, intuitive dashboard with real-time KPIs, charts, and quick access to all features",
          icon: Monitor
        },
        {
          name: "Responsive Design",
          description: "Fully responsive interface optimized for desktop, tablet, and mobile devices",
          icon: Smartphone
        },
        {
          name: "Progressive Web App (PWA)",
          description: "Installable as a native-like app on any device with offline capabilities",
          icon: Globe
        },
        {
          name: "Barcode Scanning",
          description: "Camera-based barcode scanning for quick product lookup and sales processing",
          icon: Scan
        },
        {
          name: "Receipt Generation",
          description: "Automatic receipt generation with print and PDF download options",
          icon: Receipt
        },
        {
          name: "Localized for Jamaica",
          description: "Content, pricing (JMD), and features tailored specifically for Jamaican small businesses",
          icon: Globe
        }
      ]
    },
    {
      id: 3,
      title: "Payment Methods",
      icon: CreditCard,
      color: "text-green-500",
      bgColor: "bg-green-500/10",
      features: [
        {
          name: "Stripe Integration",
          description: "Secure payment processing through Stripe - the world's leading payment platform",
          icon: CreditCard
        },
        {
          name: "Multiple Plan Options",
          description: "Three subscription tiers: Starter (J$2,500), Growth (J$5,500), Pro (J$9,500) per month",
          icon: Package
        },
        {
          name: "30-Day Free Trial",
          description: "Full-featured trial period with no credit card required to start",
          icon: Zap
        },
        {
          name: "Secure Checkout",
          description: "PCI-compliant payment processing - card details never touch our servers",
          icon: Shield
        },
        {
          name: "Automatic Subscription Management",
          description: "Seamless plan upgrades, downgrades, and subscription status tracking",
          icon: ArrowLeftRight
        },
        {
          name: "Payment Verification",
          description: "Real-time payment status polling and webhook handling for instant activation",
          icon: CheckCircle2
        }
      ]
    },
    {
      id: 4,
      title: "Operations",
      icon: Settings,
      color: "text-purple-500",
      bgColor: "bg-purple-500/10",
      features: [
        {
          name: "Goods Receipt (GR)",
          description: "Record incoming inventory from suppliers with automatic cost and quantity updates",
          icon: Package
        },
        {
          name: "Goods Issue (GI)",
          description: "Process outgoing inventory for sales, consumption, or other purposes",
          icon: ArrowLeftRight
        },
        {
          name: "Stock Transfers",
          description: "Move inventory between warehouses and storage locations with full traceability",
          icon: ArrowLeftRight
        },
        {
          name: "Inventory Adjustments",
          description: "Correct stock discrepancies with documented reasons and audit trail",
          icon: Settings
        },
        {
          name: "Cycle Counting",
          description: "Scheduled inventory counts with variance tracking and reconciliation",
          icon: BarChart3
        },
        {
          name: "Multi-Item Sales",
          description: "Cart-based sales system supporting multiple products, discounts, and receipts",
          icon: Receipt
        },
        {
          name: "Purchase Orders",
          description: "Create, track, and receive purchase orders from suppliers",
          icon: FileText
        },
        {
          name: "Sales Orders",
          description: "Manage customer orders from creation through fulfillment",
          icon: FileText
        }
      ]
    },
    {
      id: 5,
      title: "Hardware & Software",
      icon: Server,
      color: "text-orange-500",
      bgColor: "bg-orange-500/10",
      features: [
        {
          name: "No Special Hardware Required",
          description: "Works on any device with a modern web browser - no proprietary hardware needed",
          icon: Laptop
        },
        {
          name: "Multi-Device Support",
          description: "Access from smartphones, tablets, laptops, and desktop computers seamlessly",
          icon: Smartphone
        },
        {
          name: "Camera Barcode Scanning",
          description: "Uses device camera for barcode scanning - no external scanner required",
          icon: Scan
        },
        {
          name: "Cloud-Based Architecture",
          description: "Fully cloud-hosted with automatic backups, updates, and high availability",
          icon: Cloud
        },
        {
          name: "Offline PWA Support",
          description: "Core features available offline with data sync when connection restored",
          icon: Globe
        },
        {
          name: "Modern Tech Stack",
          description: "Built with React, FastAPI, and MongoDB for reliability and performance",
          icon: Server
        }
      ]
    },
    {
      id: 6,
      title: "What Makes The System Unique",
      icon: Sparkles,
      color: "text-yellow-500",
      bgColor: "bg-yellow-500/10",
      features: [
        {
          name: "Built for Jamaican Businesses",
          description: "Specifically designed for local small businesses - phone shops, clothing stores, vendors, supermarkets",
          icon: Globe
        },
        {
          name: "Moving Average Cost Valuation",
          description: "Automatic cost calculation using weighted average method for accurate profit tracking",
          icon: BarChart3
        },
        {
          name: "Instant Barcode Generation",
          description: "Generate and print professional barcodes for any product directly from the system",
          icon: Scan
        },
        {
          name: "WhatsApp Integration",
          description: "Direct WhatsApp contact button for instant customer support",
          icon: Smartphone
        },
        {
          name: "Multi-Tenant SaaS Model",
          description: "Scalable architecture supporting unlimited organizations with complete data isolation",
          icon: Users
        },
        {
          name: "Real-Time Low Stock Alerts",
          description: "Automatic notifications when products fall below reorder levels",
          icon: Zap
        }
      ]
    },
    {
      id: 7,
      title: "Systems Optimization",
      icon: Gauge,
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10",
      features: [
        {
          name: "N+1 Query Optimization",
          description: "Batch data fetching to eliminate redundant database queries - 12+ endpoints optimized",
          icon: Database
        },
        {
          name: "Kubernetes-Ready",
          description: "Health check endpoints for container orchestration and auto-scaling",
          icon: Cloud
        },
        {
          name: "Database Indexing",
          description: "Strategic MongoDB indexes on frequently queried fields for fast lookups",
          icon: Zap
        },
        {
          name: "Efficient Pagination",
          description: "Cursor-based pagination for handling large datasets without memory issues",
          icon: FileText
        },
        {
          name: "Async Operations",
          description: "Non-blocking async/await patterns throughout the backend for high concurrency",
          icon: Gauge
        },
        {
          name: "Frontend Code Splitting",
          description: "Lazy loading of page components for faster initial load times",
          icon: Monitor
        }
      ]
    }
  ];

  return (
    <div className="space-y-8" data-testid="system-specs-page">
      {/* Header */}
      <div>
        <h1 className="text-heading-black text-3xl lg:text-4xl text-[#0A0A0A] tracking-tight">SYSTEM SPECIFICATIONS</h1>
        <p className="text-[#0A0A0A]/60 mt-1">Complete technical overview of the JamInventra inventory management system</p>
      </div>

      {/* Specs Overview Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {specifications.map((spec) => (
          <Card key={spec.id} className="border border-[#E5E5EA] shadow-none hover:border-[#007AFF] transition-colors cursor-pointer" onClick={() => document.getElementById(`spec-${spec.id}`)?.scrollIntoView({ behavior: 'smooth' })}>
            <CardContent className="p-4 text-center">
              <div className={`w-10 h-10 ${spec.bgColor} rounded-lg flex items-center justify-center mx-auto mb-2`}>
                <spec.icon className={`w-5 h-5 ${spec.color}`} />
              </div>
              <p className="text-xs font-medium text-[#0A0A0A] line-clamp-2">{spec.title}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Detailed Specifications */}
      {specifications.map((spec) => (
        <Card key={spec.id} id={`spec-${spec.id}`} className="border border-[#E5E5EA] shadow-none scroll-mt-24">
          <CardHeader className="border-b border-[#E5E5EA] pb-4">
            <CardTitle className="flex items-center gap-3">
              <div className={`w-12 h-12 ${spec.bgColor} rounded-lg flex items-center justify-center`}>
                <spec.icon className={`w-6 h-6 ${spec.color}`} />
              </div>
              <div>
                <Badge className="mb-1 bg-[#0A0A0A]/5 text-[#0A0A0A]/60 text-xs">Specification {spec.id}</Badge>
                <h2 className="text-heading text-xl text-[#0A0A0A]">{spec.title}</h2>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {spec.features.map((feature, idx) => (
                <div key={idx} className="p-4 bg-[#F5F5F7] rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 ${spec.bgColor} rounded-md flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <feature.icon className={`w-4 h-4 ${spec.color}`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#0A0A0A] text-sm mb-1">{feature.name}</h4>
                      <p className="text-[#0A0A0A]/60 text-xs leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Technical Summary */}
      <Card className="border border-[#E5E5EA] shadow-none bg-gradient-to-br from-[#007AFF]/5 to-transparent">
        <CardContent className="p-6">
          <h3 className="text-heading text-lg text-[#0A0A0A] mb-4">Technical Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold text-[#0A0A0A] mb-2">Frontend</h4>
              <ul className="text-sm text-[#0A0A0A]/70 space-y-1">
                <li>• React 18 with Hooks</li>
                <li>• Tailwind CSS</li>
                <li>• Shadcn/UI Components</li>
                <li>• React Router v6</li>
                <li>• PWA with Service Worker</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#0A0A0A] mb-2">Backend</h4>
              <ul className="text-sm text-[#0A0A0A]/70 space-y-1">
                <li>• Python FastAPI</li>
                <li>• Async/Await Architecture</li>
                <li>• Pydantic Validation</li>
                <li>• JWT Authentication</li>
                <li>• Stripe Integration</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-[#0A0A0A] mb-2">Database & Infrastructure</h4>
              <ul className="text-sm text-[#0A0A0A]/70 space-y-1">
                <li>• MongoDB (NoSQL)</li>
                <li>• Cloud Hosted</li>
                <li>• Kubernetes Ready</li>
                <li>• Auto-scaling</li>
                <li>• 99.9% Uptime SLA</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
